import { LocationVarKey } from "../Generated/LocationVarKey.Data";

/**
 * Canonical enum of discount tiers a facility may offer. Each discount is
 * controlled by a single per-discount Details var: empty = not offered,
 * non-empty = offered (and the prose IS the operator's description for the
 * bot to relay). No separate toggle var.
 *
 * Display-name overrides (e.g. for the Custom discount) live in their own
 * Cortex vars referenced via `nameVarKey`.
 */

export interface DiscountDef {
  key: DiscountKey;
  label: string;
  description: string;
  /**
   * Cortex var holding operator-authored prose for the discount —
   * amount, eligibility, how to claim. EMPTY means the discount is NOT
   * offered at this facility; non-empty means the bot relays this prose
   * when asked. Surfaced via `lookup_info({topic: "discounts"})` and the
   * per-discount alias topics.
   */
  detailsVarKey: LocationVarKey;
  /** Optional Cortex var holding the operator-set display-name override. */
  nameVarKey?: LocationVarKey;
  /** Legacy on/off flag gating migration — off means "not offered", so nothing is pulled. */
  legacyHasKey?: LocationVarKey;
  /** Legacy prose var the details text migrates from (only if on AND customized off the default). */
  legacyDetailsKey?: LocationVarKey;
  /** Legacy var the display-name migrates from (Custom tier only). */
  legacyNameKey?: LocationVarKey;
}

export enum DiscountKey {
  Military = "military",
  Senior = "senior",
  Student = "student",
  LongTerm = "long_term",
  Introductory = "introductory",
  Custom = "custom"
}

export const DISCOUNTS: readonly DiscountDef[] = [
  {
    key: DiscountKey.Military,
    label: "Military discount",
    description: "Discount for active-duty and veteran military.",
    detailsVarKey: LocationVarKey.CortexDiscountMilitaryDetails,
    legacyHasKey: LocationVarKey.HasDiscountMilitary,
    legacyDetailsKey: LocationVarKey.DiscountMilitary1
  },
  {
    key: DiscountKey.Senior,
    label: "Senior discount",
    description: "Discount for senior tenants.",
    detailsVarKey: LocationVarKey.CortexDiscountSeniorDetails,
    legacyHasKey: LocationVarKey.HasDiscountSenior,
    legacyDetailsKey: LocationVarKey.DiscountSenior1
  },
  {
    key: DiscountKey.Student,
    label: "Student discount",
    description: "Discount for students.",
    detailsVarKey: LocationVarKey.CortexDiscountStudentDetails,
    legacyHasKey: LocationVarKey.HasDiscountStudent,
    legacyDetailsKey: LocationVarKey.DiscountStudent1
  },
  {
    key: DiscountKey.LongTerm,
    label: "Long-term discount",
    description: "Discount for committing to a longer rental period.",
    detailsVarKey: LocationVarKey.CortexDiscountLongTermDetails,
    legacyHasKey: LocationVarKey.HasDiscountLongTerm,
    legacyDetailsKey: LocationVarKey.DiscountLongTerm1
  },
  {
    key: DiscountKey.Introductory,
    label: "Introductory / first-month discount",
    description: "First-month or move-in special.",
    detailsVarKey: LocationVarKey.CortexDiscountIntroductoryDetails,
    legacyHasKey: LocationVarKey.HasDiscountIntroductory,
    legacyDetailsKey: LocationVarKey.DiscountIntroductory1
  },
  {
    key: DiscountKey.Custom,
    label: "Custom discount",
    description: "Operator-defined custom discount.",
    detailsVarKey: LocationVarKey.CortexDiscountCustomDetails,
    nameVarKey: LocationVarKey.CortexDiscountCustomName,
    legacyHasKey: LocationVarKey.HasDiscountCustom,
    legacyDetailsKey: LocationVarKey.DiscountCustomDiscount1,
    legacyNameKey: LocationVarKey.DiscountCustomDisplayName
  }
];

export function findDiscountDef(key: DiscountKey): DiscountDef | undefined {
  return DISCOUNTS.find((d) => d.key === key);
}
